import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import DriveMode from './pages/DriveMode';
import AudioLibrary from './pages/AudioLibrary';
import Settings from './pages/Settings';
import ProfileSettings from './pages/ProfileSettings';
import ContactUs from './pages/ContactUs';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TakedownPolicy from './pages/TakedownPolicy';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AuthCallback from './pages/AuthCallback';
import AdminDashboard from './pages/AdminDashboard';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { initAnalytics, isFirstVisit, markAsVisited, trackPageView } from './services/analytics';

// Component to handle first visit redirect and page tracking
function FirstVisitHandler({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Initialize analytics on first load
    initAnalytics();
  }, []);
  
  useEffect(() => {
    // Track page views
    const pageName = location.pathname === '/' ? 'home' : location.pathname.replace('/', '').replace(/-/g, '_');
    trackPageView(pageName);
  }, [location.pathname]);
  
  useEffect(() => {
    // Removed first visit redirect to login - users start directly on home
    // Mark as visited for analytics
    if (isFirstVisit()) {
      markAsVisited();
    }
  }, [location.pathname, location.hash, navigate]);
  
  return children;
}

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This must be done synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <FirstVisitHandler>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/drive-mode" element={<DriveMode />} />
        <Route path="/audio-library" element={<AudioLibrary />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/profile" element={<ProfileSettings />} />
        <Route path="/settings/contact" element={<ContactUs />} />
        <Route path="/settings/terms" element={<TermsAndConditions />} />
        <Route path="/settings/privacy" element={<PrivacyPolicy />} />
        <Route path="/settings/takedown" element={<TakedownPolicy />} />
      </Routes>
    </FirstVisitHandler>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;